import { DatePicker, KpiCard } from '@components/common';
import {
  useBookings,
  useDashboardStats,
  useRevParReport,
  useReviewStats,
  useRooms,
} from '@hooks/accommodation';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { OCCUPANCY_MAX_DAYS } from '@lib/accommodationConstants';
import { addDaysISO, isoToDate, todayISO, normalizeLabel } from '@lib/utils';
import { selectAccommodationProperty } from '@store/accommodation';
import {
  selectAllOutlets,
  selectCanUseCompanyScope,
  selectEffectiveOutletId,
  setSelectedOutletId,
} from '@store/outlets';
import { Building2, DollarSign, Star, TrendingUp, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdvancePaymentsAlert } from './AdvancePaymentsAlert';
import { DistributionPieChart, PieDataItem } from './DistributionPieChart';
import { OccupancyChart } from './OccupancyChart';
import { RecentArrivals } from './RecentArrivals';
import { RevParTrendChart } from './RevParTrendChart';
import { RevenueCard } from './RevenueCard';
import { StatusSummaryChips } from './StatusSummaryChips';
import { WeeklyCalendar } from './WeeklyCalendar';

export const AccommodationDashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const outlets = useAppSelector(selectAllOutlets);
  const canUseCompanyScope = useAppSelector(selectCanUseCompanyScope);
  const effectiveOutletId = useAppSelector(selectEffectiveOutletId);
  const property = useAppSelector(selectAccommodationProperty);

  // Date range — default: -30 days → today (trailing window, matches other dashboards)
  const today = todayISO();
  const [startDate, setStartDate] = useState<string>(addDaysISO(today, -30));
  const [endDate, setEndDate] = useState<string>(today);

  const from = startDate;
  const to = endDate || startDate;

  const { data: bookings = [], isLoading: isBookingsLoading } = useBookings(
    property?.id,
  );
  const { data: rooms = [] } = useRooms(property?.id);
  const { data: stats, isLoading: isDashboardLoading } = useDashboardStats(
    property?.id,
    from,
    to,
  );
  const { data: revParRows = [], isLoading: isRevParLoading } = useRevParReport(
    property?.id,
    from,
    to,
  );
  const { data: reviewStats, isLoading: isReviewsLoading } = useReviewStats(
    property?.id,
  );

  useEffect(() => {
    if (!canUseCompanyScope) return;
    if (effectiveOutletId || outlets.length === 0) return;
    dispatch(setSelectedOutletId(outlets[0].id));
  }, [canUseCompanyScope, dispatch, effectiveOutletId, outlets]);

  // ── Occupancy chart data ────────────────────────────────────────────────────
  const occupancyData = useMemo(() => {
    const activeRoomCount = rooms.filter((r) => r.is_active).length;
    if (activeRoomCount === 0) return [];
    const start = isoToDate(from);
    const end = isoToDate(to);
    const days: Array<{ label: string; Booked: number; Available: number }> =
      [];
    let count = 0;
    for (
      let d = new Date(start);
      d <= end && count < OCCUPANCY_MAX_DAYS;
      d.setDate(d.getDate() + 1), count += 1
    ) {
      const dayStr = d.toISOString().slice(0, 10);
      const bookedRoomIds = new Set<string>();
      bookings.forEach((b) => {
        if (b.status === 'cancelled' || b.status === 'no_show') return;
        if (b.check_in <= dayStr && b.check_out > dayStr) {
          (b.booking_rooms ?? []).forEach((br) =>
            bookedRoomIds.add(br.room_id),
          );
        }
      });
      const booked = Math.min(bookedRoomIds.size, activeRoomCount);
      days.push({
        label: String(d.getDate()).padStart(2, '0'),
        Booked: booked,
        Available: activeRoomCount - booked,
      });
    }
    return days;
  }, [bookings, rooms, from, to]);

  // ── Period check-in count (for KPI card) ───────────────────────────────────
  const checkInCount = useMemo(
    () =>
      bookings.filter(
        (b) =>
          b.check_in >= from &&
          b.check_in <= to &&
          b.status !== 'cancelled' &&
          b.status !== 'no_show',
      ).length,
    [bookings, from, to],
  );

  // ── Advance payments (paid, not yet checked in) ─────────────────────────────
  const advancePayments = useMemo(() => {
    let total = 0;
    let count = 0;
    bookings.forEach((b) => {
      if (b.status === 'pending' || b.status === 'confirmed') {
        const paid = b.amount_paid ?? 0;
        if (paid > 0) {
          total += paid;
          count += 1;
        }
      }
    });
    return { total, count };
  }, [bookings]);

  // ── Pie data: booking sources ───────────────────────────────────────────────
  const sourcePieData = useMemo<PieDataItem[]>(() => {
    if (!stats?.bookings_by_source?.length) return [];
    const groupedSources = stats.bookings_by_source.reduce<
      Map<string, { name: string; value: number }>
    >((map, source) => {
      const name = normalizeLabel(source.source_name, 'Unknown');
      const key = name.toLowerCase();
      const current = map.get(key) || { name, value: 0 };

      current.value += source.booking_count;
      map.set(key, current);

      return map;
    }, new Map());

    const total = Array.from(groupedSources.values()).reduce(
      (sum, item) => sum + item.value,
      0,
    );

    return Array.from(groupedSources.values())
      .map((item) => ({
        name: item.name,
        value: item.value,
        pct: total > 0 ? Math.round((item.value / total) * 100) : 0,
      }))
      .sort((left, right) => right.value - left.value);
  }, [stats]);

  // ── Pie data: guest countries ───────────────────────────────────────────────
  const countryPieData = useMemo<PieDataItem[]>(() => {
    if (!stats?.top_countries?.length) return [];
    const groupedCountries = stats.top_countries.reduce<
      Map<string, { name: string; value: number }>
    >((map, country) => {
      const name = normalizeLabel(country.country_name, 'Unknown');
      const key = name.toLowerCase();
      const current = map.get(key) || { name, value: 0 };

      current.value += country.count;
      map.set(key, current);

      return map;
    }, new Map());

    const groupedCountryList = Array.from(groupedCountries.values()).sort(
      (left, right) => right.value - left.value,
    );
    const total = groupedCountryList.reduce((sum, item) => sum + item.value, 0);

    return groupedCountryList.slice(0, 8).map((item) => ({
      name: item.name,
      value: item.value,
      pct: total > 0 ? Math.round((item.value / total) * 100) : 0,
    }));
  }, [stats]);

  // ── Max booking count per source (for progress bars) ───────────────────────
  const maxSourceCount = useMemo(
    () =>
      sourcePieData.reduce(
        (maxValue, item) => Math.max(maxValue, item.value),
        1,
      ),
    [sourcePieData],
  );

  // ── Upcoming & recent arrivals (sorted by check-in, asc) ───────────────────
  const recentArrivals = useMemo(
    () =>
      bookings
        .filter(
          (b) =>
            b.status === 'checked_in' ||
            b.status === 'confirmed' ||
            b.status === 'pending',
        )
        .sort(
          (a, b2) =>
            new Date(a.check_in).getTime() - new Date(b2.check_in).getTime(),
        )
        .slice(0, 8),
    [bookings],
  );

  const currency = property?.currency ?? '';
  const checkOutTime = property?.check_out_time ?? '11:00';
  const periodLabel =
    startDate && endDate ? `${startDate} – ${endDate}` : startDate;
  const totalActiveRooms = rooms.filter((room) => room.is_active).length;

  const revParTrendData = useMemo(() => {
    const rowsByDate = new Map(revParRows.map((row) => [row.report_date, row]));
    const start = isoToDate(from);
    const end = isoToDate(to);
    const points: Array<{ label: string; revpar: number }> = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.toISOString().slice(0, 10);
      const row = rowsByDate.get(day);
      points.push({
        label: String(d.getDate()).padStart(2, '0'),
        revpar: row?.revpar ?? 0,
      });
    }

    return points;
  }, [revParRows, from, to]);

  const avgRevPar = useMemo(() => {
    if (revParTrendData.length === 0 || totalActiveRooms === 0) return 0;
    const totalRevenue = revParRows.reduce(
      (sum, row) => sum + row.total_room_revenue,
      0,
    );
    return totalRevenue / (totalActiveRooms * revParTrendData.length);
  }, [revParRows, revParTrendData.length, totalActiveRooms]);

  return (
    <div className="space-y-4">
      {/* ── Page header + date range picker ─────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {property?.name ?? 'Dashboard'}
          </h2>
          <p className="text-xs text-gray-400">
            {new Date().toLocaleDateString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>

        {/* Range picker — popover is right-anchored inside DatePicker */}
        <DatePicker
          mode="range"
          startDate={startDate}
          endDate={endDate}
          onRangeChange={(s, e) => {
            setStartDate(s);
            setEndDate(e ?? s);
          }}
          placeholder="Select period"
          label=""
          id="dashboard-period"
        />
      </div>
      {/* ── Top section: KPIs + occupancy (left) / calendar (right) ─── */}
      {/* The calendar column only stretches to the height of this section */}
      <div className="grid gap-4 xl:grid-cols-[1fr_288px]">
        {/* Left: KPIs, alert, occupancy */}
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              title="Total Bookings"
              value={isDashboardLoading ? '…' : (stats?.total_bookings ?? 0)}
              sub={periodLabel}
              icon={TrendingUp}
              accent="bg-pos-primary"
            />
            <KpiCard
              title="Check Ins"
              value={checkInCount}
              sub="in period"
              icon={Users}
              accent="bg-teal-600"
            />
            <KpiCard
              title="Revenue"
              value={
                isDashboardLoading
                  ? '…'
                  : `${currency} ${(stats?.total_revenue ?? 0).toLocaleString()}`
              }
              sub={`${currency} ${(stats?.total_paid ?? 0).toLocaleString()} collected`}
              icon={DollarSign}
              accent="bg-pos-secondary"
            />
            <KpiCard
              title="Avg. RevPAR"
              value={
                isRevParLoading
                  ? '…'
                  : `${currency} ${Math.round(avgRevPar).toLocaleString()}`
              }
              sub={periodLabel}
              icon={Building2}
              accent="bg-emerald-600"
            />
          </div>

          <AdvancePaymentsAlert
            count={advancePayments.count}
            total={advancePayments.total}
            currency={currency}
          />

          <OccupancyChart data={occupancyData} isLoading={isBookingsLoading} />
        </div>

        {/* Right: weekly calendar — height is governed by left column above */}
        <WeeklyCalendar
          rooms={rooms}
          bookings={bookings}
          checkOutTime={checkOutTime}
        />
      </div>

      {/* ── Bottom section: full-width rows ─────────────────────────── */}

      {/* Revenue overview + recent arrivals */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RevenueCard
          stats={stats ?? null}
          isLoading={isDashboardLoading}
          currency={currency}
          maxSourceCount={maxSourceCount}
          sourceBreakdown={sourcePieData}
        />
        <RecentArrivals
          bookings={recentArrivals}
          isLoading={isBookingsLoading}
        />
      </div>

      {/* ── Reviews summary card ─────────────────────────────────── */}
      <div
        className={`rounded-2xl border border-gray-200 bg-white p-5 ${isReviewsLoading ? 'animate-pulse opacity-60 pointer-events-none' : ''}`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Star size={16} className="text-amber-400" />
            <span className="text-sm font-semibold text-gray-900">
              Guest Reviews
            </span>
          </div>
          <Link
            to="/accommodation/reviews"
            className="text-xs font-medium text-pos-primary hover:underline"
          >
            View all →
          </Link>
        </div>

        {reviewStats ? (
          <div className="mt-4 flex flex-wrap items-center gap-6">
            {/* Total & response rate */}
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {reviewStats.total_reviews ?? 0}
              </p>
              <p className="text-xs text-gray-400">
                {reviewStats.total_checkouts
                  ? `${reviewStats.total_reviews} of ${reviewStats.total_checkouts} checkouts reviewed`
                  : 'reviews collected'}
              </p>
            </div>

            {/* Average score */}
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">
                {reviewStats.avg_computed != null
                  ? Number(reviewStats.avg_computed).toFixed(1)
                  : '—'}
              </span>
              <span className="text-xs text-gray-400">/ 5</span>
              <div className="ml-1 flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={12}
                    className={
                      n <= Math.round(reviewStats.avg_computed ?? 0)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-200'
                    }
                  />
                ))}
              </div>
            </div>

            {/* Star distribution mini-bars */}
            <div className="flex flex-1 flex-col gap-1 min-w-[160px]">
              {(
                [
                  { label: '5★', count: reviewStats.five_star_count ?? 0 },
                  { label: '4★', count: reviewStats.four_star_count ?? 0 },
                  { label: '3★', count: reviewStats.three_star_count ?? 0 },
                  { label: '2★', count: reviewStats.two_star_count ?? 0 },
                  { label: '1★', count: reviewStats.one_star_count ?? 0 },
                ] as const
              ).map(({ label, count }) => {
                const pct =
                  (reviewStats.total_reviews ?? 0) > 0
                    ? Math.round(
                        (count / (reviewStats.total_reviews ?? 1)) * 100,
                      )
                    : 0;
                return (
                  <div key={label} className="flex items-center gap-2">
                    <span className="w-5 text-right text-[10px] text-gray-400">
                      {label}
                    </span>
                    <div className="h-1.5 flex-1 rounded-full bg-gray-100">
                      <div
                        className="h-1.5 rounded-full bg-amber-400"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-6 text-[10px] text-gray-400">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-400">
            No reviews yet — they&apos;ll appear here after guests check out.
          </p>
        )}
      </div>

      <RevParTrendChart
        title="RevPAR Trend"
        data={revParTrendData}
        isLoading={isRevParLoading}
      />

      {/* Distribution pie charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <DistributionPieChart
          title="Bookings by Source"
          data={sourcePieData}
          isLoading={isDashboardLoading}
        />
        <DistributionPieChart
          title="Guest Countries"
          data={countryPieData}
          isLoading={isDashboardLoading}
        />
      </div>

      {/* Status summary chips */}
      <StatusSummaryChips
        stats={stats ?? null}
        isLoading={isDashboardLoading}
      />
    </div>
  );
};
